import React, { useState } from "react";

import { Code } from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import CLICode from "Components/Code";
import { CLI_COMMAND_NAME } from "Constants/documentationUrls";
import EnvironmentRoutes from "Containers/EnvironmentRoutes";
import * as Dropdown from "ds/Dropdown";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import getUrl from "Libs/getUrl";
import { getEnvironmentGitCommand } from "Libs/utils";
import { meSelector } from "Reducers/app/selectors";
import {
  environmentLoadingSelector,
  environmentSelector
} from "Reducers/environment";

import CLISection from "./CLISection";
import * as S from "./InfoDropButtons.styles";
import SshSection from "./SshSection";
import { sortCLICommands, sortSSHCommands } from "./utils";

const DropdownButtons = ({ project, noPadding }) => {
  const intl = useIntl();
  const { organizationId, environmentId } = useDecodedParams();
  const environment = useSelectorWithUrlParams(environmentSelector);
  const mainEnvironment = useSelectorWithUrlParams(environmentSelector, {
    environmentId: project.default_branch
  });
  const isMainEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector,
    {
      environmentId: project.default_branch
    }
  );
  const username = useSelector(meSelector).username;
  const [searchTitle, setSearchTitle] = useState();
  const sshUrls = environment ? environment.getSshUrls() : {};
  const onProjectOverview = !environment;
  const [userSshUrl] = useUrls(["users.profile.sshKeys"], {
    username: username || ""
  });

  const sortedSSHCommands = sortSSHCommands(sshUrls);
  const sortedCLICommands = sortCLICommands({
    sshUrls,
    projectId: project.id,
    environmentName: environment?.name,
    intl
  });

  const sshCommands = sshUrls
    ? Object.keys(sshUrls).map(key => sshUrls[key].replace("ssh://", "ssh "))
    : [];

  const cliCommand = getEnvironmentGitCommand(
    project,
    environment,
    intl.formatMessage({
      id: "CLI_command_name"
    }),
    environmentId ? "git" : "git-project"
  );
  const platformCliCommand = getEnvironmentGitCommand(
    project,
    environment,
    intl.formatMessage({
      id: "CLI_command_name"
    }),
    environmentId ? "cli" : "cli-project"
  );
  const canSsh = environment?._links?.ssh?.href;

  return (
    <S.Layout aria-label="Environment buttons" noPadding={noPadding}>
      <S.Wrapper className="info-buttons">
        <Dropdown.CommandMenu
          triggerLabel={intl.formatMessage({ id: "urls" })}
          headerContent={
            <div>
              {environmentId && (
                <S.LinkSSH
                  to={getUrl({
                    key: "organization.project.environment.services.appName",
                    props: {
                      organizationId,
                      projectId: project.id,
                      environmentId,
                      appName: "routes"
                    }
                  })}
                  analyticId="dropdown.routes.overview"
                >
                  {intl.formatMessage({ id: "dropdown.routes.overview" })}
                </S.LinkSSH>
              )}
              <S.LinkDoc
                href={intl.formatMessage({
                  id: "links.documentation.define_routes"
                })}
                analyticId="links.documentation.define_routes"
              >
                {intl.formatMessage({ id: "ssh_documentation" })}
              </S.LinkDoc>
            </div>
          }
          tabs={[
            {
              id: "url-section-cli",
              label: intl.formatMessage({ id: "cli" }),
              component: (
                <S.ContentWrapper>
                  <S.Description>
                    {intl.formatMessage({
                      id: "dropdown.routes.description"
                    })}
                  </S.Description>
                  <Code.Block
                    id="environment-cli-routes"
                    isCopyable
                    singleLine={true}
                  >
                    {intl.formatMessage(
                      {
                        id: "dropdown.routes.code"
                      },
                      {
                        cli_command: CLI_COMMAND_NAME,
                        projectId: project.id
                      }
                    )}
                  </Code.Block>
                </S.ContentWrapper>
              )
            },
            {
              id: "url-section-preview",
              label: intl.formatMessage({ id: "preview" }),
              component: (
                <S.ContentWrapper>
                  <EnvironmentRoutes
                    projectId={project.id}
                    organizationId={organizationId}
                    environmentId={environmentId}
                    hideRedirects
                  />
                </S.ContentWrapper>
              )
            }
          ]}
        />

        {environment &&
          sshCommands.length > 0 &&
          (canSsh ? (
            <Dropdown.CommandMenu
              triggerLabel={intl.formatMessage({ id: "ssh" })?.toUpperCase()}
              headerContent={
                <div>
                  <S.LinkSSH href={userSshUrl} analyticId="add_ssh_key">
                    {intl.formatMessage({ id: "add_ssh_key" })}
                  </S.LinkSSH>
                  <S.LinkDoc
                    href={intl.formatMessage({
                      id: "links.documentation.add_ssh_keys"
                    })}
                    analyticId="links.documentation.add_ssh_key"
                  >
                    {intl.formatMessage({ id: "ssh_documentation" })}
                  </S.LinkDoc>
                </div>
              }
              tabs={[
                {
                  id: "cli-section-cli",
                  label: intl.formatMessage({ id: "cli" }),
                  component: (
                    <S.ContentWrapper>
                      <CLISection
                        sortedCLICommands={sortedCLICommands}
                        searchTitle={searchTitle}
                        setSearchTitle={setSearchTitle}
                        commandsLength={sshCommands.length}
                      />
                    </S.ContentWrapper>
                  )
                },
                {
                  id: "cli-section-ssh",
                  label: intl.formatMessage({ id: "ssh" }),
                  component: (
                    <S.ContentWrapper>
                      <SshSection
                        sortedSSHCommands={sortedSSHCommands}
                        searchTitle={searchTitle}
                        setSearchTitle={setSearchTitle}
                        commandsLength={sshCommands.length}
                      />
                    </S.ContentWrapper>
                  )
                }
              ]}
            />
          ) : (
            <Tooltip tooltip={intl.formatMessage({ id: "ssh.disabled" })}>
              <Dropdown.CommandMenu
                triggerLabel={intl.formatMessage({ id: "ssh" })?.toUpperCase()}
                tabs={[{ id: "" }]}
                disabled
              />
            </Tooltip>
          ))}

        {environment && (
          <Dropdown.CommandMenu
            triggerLabel={intl.formatMessage({
              id: "dropdown.container_log.label"
            })}
            headerContent={
              <div>
                <S.LinkDoc
                  href={intl.formatMessage({
                    id: "links.documentation.container_log"
                  })}
                  analyticId="links.documentation.container_log"
                >
                  {intl.formatMessage({ id: "ssh_documentation" })}
                </S.LinkDoc>
              </div>
            }
            tabs={[
              {
                id: "cli",
                label: intl.formatMessage({ id: "cli" }),
                component: (
                  <S.ContentWrapper>
                    <S.Description>
                      {intl.formatMessage({
                        id: "dropdown.container_log.description"
                      })}
                    </S.Description>
                    <Code.Block
                      id="environment-cli-container_log"
                      isCopyable
                      singleLine={true}
                    >
                      {intl.formatMessage(
                        {
                          id: "dropdown.container_log.code"
                        },
                        {
                          cli_command: CLI_COMMAND_NAME,
                          projectId: project.id,
                          environmentId: environment?.name
                        }
                      )}
                    </Code.Block>
                  </S.ContentWrapper>
                )
              }
            ]}
          />
        )}

        {onProjectOverview && (
          <>
            <Tooltip
              tooltip={intl.formatMessage({
                id:
                  !mainEnvironment?.has_code || isMainEnvironmentLoading
                    ? "dropdown.disabled.ssh.not_ready"
                    : "dropdown.disabled.ssh"
              })}
            >
              <Dropdown.CommandMenu
                triggerLabel={intl.formatMessage({ id: "ssh" })?.toUpperCase()}
                tabs={[{ id: "" }]}
                disabled
              />
            </Tooltip>
            <Tooltip
              tooltip={intl.formatMessage({
                id:
                  !mainEnvironment?.has_code || isMainEnvironmentLoading
                    ? "dropdown.disabled.logs.not_ready"
                    : "dropdown.disabled.logs"
              })}
            >
              <Dropdown.CommandMenu
                triggerLabel={intl.formatMessage({
                  id: "dropdown.container_log.label"
                })}
                tabs={[{ id: "" }]}
                disabled
              />
            </Tooltip>
          </>
        )}

        {cliCommand && platformCliCommand && (
          <CLICode
            gitCommand={cliCommand}
            platformCommand={platformCliCommand}
          />
        )}
      </S.Wrapper>
    </S.Layout>
  );
};

DropdownButtons.propTypes = {
  project: PropTypes.object.isRequired,
  noPadding: PropTypes.bool,
  isProjectNotReady: PropTypes.bool
};

export default DropdownButtons;
